<script lang="ts">
	import { getTranslate, T } from '@tolgee/svelte';
	import { cart } from '$lib/store/cart.store';
	import MerchandiseSummary from '$lib/components/Cart/MerchandiseSummary.svelte';
	import ButtonPrimary from '$lib/components/Button/ButtonPrimary.svelte';
	import { transformProductCollectionsToBranch } from '$lib/utils/product';
	import { page } from '$app/stores';
	import ProductCardHorizontalLineItem from '$lib/components/Product/ProductCardHorizontalLineItem.svelte';
	import DiscountInput from '$lib/components/Cart/DiscountInput.svelte';
	import { resolveRoute } from '$app/paths';
	import ProductCardGenericSlider from '$lib/components/Slider/ProductCardGenericSlider.svelte';
	import ProductCardVertical from '$lib/components/Product/ProductCardVertical.svelte';
	import { classes } from '@thearc-hq/ui-kit/utils';
	import ProductBundleRecommendationCardSlider from '$lib/components/Product/SpecialSale/ProductBundleRecommendationCardSlider.svelte';
	import { customer } from '$lib/store/customer.store';
	import ButtonSecondary from '$lib/components/Button/ButtonSecondary.svelte';
	import { customerSessionDrawer } from '$lib/store/customerSessionDrawer.store';
	import Slider from '$lib/components/Storyblok/Slider.svelte';
	import { recordStartCheckout } from '$lib/posthog';

	const { t } = getTranslate();

	$: crossSellStores = $cart.crossSellProducts;
	$: specialSale = $cart.specialSale;
</script>

<svelte:head>
	<title>{$t('seo.title.cart')}</title>
</svelte:head>
<div class="md:mt-4 space-y-0 md:pt-6 lg:space-y-16">
	<div class="flex space-x-6 box-x-wrapper">
		<h2 class="text-h-24 lg:text-h-40">
			{$cart.totalQuantity > 0 ? $t('cart.title') : $t('cart.emptyTitle')}
		</h2>
	</div>
	<div class="grid md:grid-cols-3 gap-x-8 box-x-wrapper">
		<!-- TODO: Section for Product Lines and SpecialSale -->
		<div class="md:col-span-2 space-y-4">
			{#each $cart.lines as cartItem (`${cartItem.id}-${cartItem.quantity}`)}
				<ProductCardHorizontalLineItem
					data={cartItem}
					quantity={cartItem.quantity}
					discountAllocations={cartItem.discountAllocations}
				/>
			{/each}

			<div class="hidden md:block">
				{#if $specialSale?.length > 0}
					<hr />
					<ProductBundleRecommendationCardSlider responses={$specialSale} />
				{/if}
			</div>
		</div>

		<div class="grid md:hidden">
			{#if $specialSale?.length > 0}
				<hr />
				<ProductBundleRecommendationCardSlider responses={$specialSale} />
			{/if}
		</div>

		<!-- TODO: Section for Cart Summary -->
		<div class="space-y-4">
			<h5 class="text-h-20">
				<T keyName="checkout.summary" />
			</h5>
			<MerchandiseSummary />

			<a
				on:click={() => {
					recordStartCheckout();
				}}
				href={resolveRoute('/[locale]-[country]/checkout', {
					...$page.params
				})}
			>
				<ButtonPrimary
					iconPosition="right"
					class="w-full group overflow-hidden"
					text={$customer ? $t('cart.pay') : $t('cart.payAsGuest')}
					disabled={$cart.totalQuantity === 0}
				>
					<div
						slot="icon"
						class="text-current !h-6 !w-6 group-active:translate-x-48 group-focus:translate-x-48 transition-transform ease-nice duration-500 i-sprenger-creditcard"
					></div>
				</ButtonPrimary>
			</a>

			<hr />

			<DiscountInput />

			<div class="space-y-4 md:space-y-2 py-8">
				<p class="flex space-x-2">
					<span class="i-sprenger-rotate-ccw w-6 h-6 flex-none" />
					<span class="text-h-16">
						<T keyName="uniqueSellingPoints.warranty" />
					</span>
				</p>
				<p class="flex space-x-2">
					<span class="i-sprenger-lock w-6 h-6 flex-none" />
					<span class="text-h-16">
						<T keyName="uniqueSellingPoints.ssl" />
					</span>
				</p>
				<p class="flex space-x-2">
					<span class="i-sprenger-package w-6 h-6 flex-none" />
					<span class="text-h-16">
						<T keyName="uniqueSellingPoints.returnOrder" />
					</span>
				</p>
			</div>

			{#if !$customer}
				<div class="p-4 space-y-4 border rounded-lg w-full my-12">
					<div class="space-y-2">
						<h5 class="text-h-16 text-dark-primary">
							<T keyName="cart.noticeUnregisteredUser.title" />
						</h5>

						<ul class="pl-6 list-disc text-p-14 text-dark-black">
							{@html $t('cart.noticeUnregisteredUser.listItems')}
						</ul>
					</div>

					<div class="flex flex-col w-full space-y-3">
						<ButtonSecondary
							text={$t('signUpForm_for_free')}
							on:click={() => {
								customerSessionDrawer.setSessionType('register');
								customerSessionDrawer.setIsExpanded(true);
							}}
						/>
						<ButtonPrimary
							text={$t('signUpForm_signIn')}
							on:click={() => {
								customerSessionDrawer.setSessionType('login');
								customerSessionDrawer.setIsExpanded(true);
							}}
						/>
					</div>
				</div>
			{/if}
		</div>
	</div>
	{#if $crossSellStores?.length > 0}
		<ProductCardGenericSlider
			title={$crossSellStores?.length > 0
				? $t('product.matchingProducts')
				: $t('product.recommendedProducts.empty')}
		>
			{#each $crossSellStores || [] as store}
				{#if store?.data?.product?.product}
					<ProductCardVertical
						{...store?.data?.product?.product}
						branch={transformProductCollectionsToBranch(store.data?.product?.product?.collections)}
						sku={store.data.sku}
						class={classes('default-slider-card', 'snap-start')}
						sourceExtra="Cart Cross-Sell"
					/>
				{/if}
			{/each}
		</ProductCardGenericSlider>
	{:else}
		<Slider blok={{ collection_slug: 'hundesport-selection', title: $t('cart.youCouldLikeThis') }}
		></Slider>
	{/if}
</div>
